import React, { useState, useEffect, useContext } from "react";
import { teethData } from "../../../data";
import { AuthContext } from "../../../context/AuthContext";
import Dropdown from "./Dropdown";
import AddCategoryItemModal from "./AddCategoryItemModal";

// Tooth Component
const Tooth = ({ id, upperImg, frontImg, onSelect, isSelected }) => {
  return (
    <div
      className={`cursor-pointer p-2 border ${isSelected ? "border-red-700 border-4" : "border-gray-100 border-2"
        } rounded-lg hover:border-blue-700 transition duration-300`}
      onClick={() => onSelect(id)} // This will toggle the selection
      title={isSelected ? "Deselect Tooth" : "Select Tooth"} // Tooltip to indicate whether the tooth is selected or not
    >
      <div className="flex flex-col items-center">
        <img
          src={upperImg}
          alt={`Tooth ${id} upper`}
          className="w-16 h-16 object-contain mb-2"
        />
        <img
          src={frontImg}
          alt={`Tooth ${id} front`}
          className="w-16 h-16 object-contain mb-2"
        />
        <div className="bg-gray-100 p-1 rounded-full">
          <span className="text-sm font-semibold text-gray-700">{id}</span>
        </div>
      </div>
    </div>
  );
};

// ToothGrid Component
const ToothGrid = ({ selectedTeeth, setSelectedTeeth, patientAge }) => {
  const { dentalReasons, primaryDiagnosis, treatmentPlan } = useContext(AuthContext);
const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("allergiesName");


  const treatmentOptions = [
    "Filling",
    "Root Canal Treatment",
    "Extraction",
    "Scaling & Polishing",
    "Crown",
    "Observation",
  ];

  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [selectedToothId, setSelectedToothId] = useState(null);
  const [reasons, setReasons] = useState([]); // array for multiple reasons
  const [currentReason, setCurrentReason] = useState(""); // For adding new reasons
  const [showChildTeeth, setShowChildTeeth] = useState(false);

  // New states for diagnosis & treatment
  const [currentDiagnosis, setCurrentDiagnosis] = useState("");
  const [currentTreatment, setCurrentTreatment] = useState("");

  // Set teeth type display based on patient age when component mounts or age changes
  useEffect(() => {
    if (patientAge === undefined || patientAge === null) {
      setShowChildTeeth(false);
      return;
    }
    // Keep manual toggle by user; we don't auto-toggle selection here
  }, [patientAge]);

  // Open modal and prefill if tooth is already selected
  const handleSelectTooth = (id) => {
    const selectedTooth = selectedTeeth.find((tooth) => tooth.id === id);

    if (selectedTooth) {
      setSelectedToothId(id);
      setReasons(selectedTooth.reasons || []);
      setCurrentDiagnosis(selectedTooth.diagnosis || "");
      setCurrentTreatment(selectedTooth.treatment || "");
    } else {
      setSelectedToothId(id);
      setReasons([]);
      setCurrentDiagnosis("");
      setCurrentTreatment("");
    }

    setCurrentReason("");
    setReasonModalOpen(true);
  };

  // Reason handlers
  const handleCurrentReasonChange = (event) => {
    setCurrentReason(event.target.value);
  };
  const handleAddReason = () => {
    const trimmed = currentReason.trim();
    if (trimmed && !reasons.includes(trimmed)) {
      setReasons((prev) => [...prev, trimmed]);
      setCurrentReason("");
    }
  };
  const handleRemoveReason = (reasonToRemove) => {
    setReasons((prev) => prev.filter((r) => r !== reasonToRemove));
  };

  // Diagnosis & Treatment change handlers
  const handleDiagnosisChange = (e) => setCurrentDiagnosis(e.target.value);
  const handleTreatmentChange = (e) => setCurrentTreatment(e.target.value);

  // Save or update the reasons + diagnosis + treatment for the selected tooth
  const handleSaveReasons = () => {
    // If no reasons and no diagnosis and no treatment, remove the tooth from selection
    const noData =
      (!reasons || reasons.length === 0) &&
      !currentDiagnosis &&
      !currentTreatment;

    if (noData) {
      handleRemoveTooth();
      return;
    }

    setSelectedTeeth((prevSelected) => {
      const updatedSelectedTeeth = [...prevSelected];
      const toothIndex = updatedSelectedTeeth.findIndex(
        (tooth) => tooth.id === selectedToothId
      );

      const updatedEntry = {
        id: selectedToothId,
        reasons: [...reasons],
        diagnosis: currentDiagnosis || "",
        treatment: currentTreatment || "",
      };

      if (toothIndex >= 0) {
        updatedSelectedTeeth[toothIndex] = updatedEntry;
      } else {
        updatedSelectedTeeth.push(updatedEntry);
      }

      return updatedSelectedTeeth;
    });

    // Close modal and reset temp states
    setReasonModalOpen(false);
    setReasons([]);
    setCurrentReason("");
    setCurrentDiagnosis("");
    setCurrentTreatment("");
  };

  // Remove the selected tooth
  const handleRemoveTooth = () => {
    setSelectedTeeth((prevSelected) =>
      prevSelected.filter((tooth) => tooth.id !== selectedToothId)
    );

    // Close modal and reset
    setReasonModalOpen(false);
    setReasons([]);
    setCurrentReason("");
    setCurrentDiagnosis("");
    setCurrentTreatment("");
  };

  // Toggle between adult and child teeth
  const handleTeethTypeChange = (event) => {
    setShowChildTeeth(event.target.checked);
  };

  // Function to render the teeth components
  const renderTeeth = (teeth) => {
    return teeth.map((tooth) => (
      <td key={tooth.id} className="p-1">
        <Tooth
          id={tooth.id}
          upperImg={tooth.upperImg}
          frontImg={tooth.frontImg}
          onSelect={handleSelectTooth}
          isSelected={selectedTeeth.some(
            (selectedTooth) => selectedTooth.id === tooth.id
          )} // Check if the tooth is selected
        />
      </td>
    ));
  };

  return (
    <div className="w-full">
      {/* Teeth Type Selection with Age Indicator */}
      <div className="flex flex-col sm:flex-row sm:items-center mb-4 gap-4">
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <span className="mr-3 text-sm font-medium text-gray-700">
              Adult Teeth
            </span>
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={showChildTeeth}
                onChange={handleTeethTypeChange}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
            <span className="ml-3 text-sm font-medium text-gray-700">
              Child Teeth
            </span>
          </label>
        </div>
      </div>

      <div className="overflow-x-auto">
        {!showChildTeeth ? (
          // Adult Teeth Display
          <>
            {/* Upper Teeth */}
            <div className="min-w-max">
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="border-r-4 border-b-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.upperLeftTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                    <td className="border-l-2 border-b-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.upperRightTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Lower Teeth */}
            <div className="min-w-max">
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="border-r-4 border-t-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.lowerLeftTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                    <td className="border-t-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.lowerRightTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : (
          // Child Teeth Display
          <>
            {/* Upper Teeth */}
            <div className="min-w-max">
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="border-r-4 border-b-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.childUpperLeftTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                    <td className="border-l-2 border-b-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.childUpperRightTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Lower Teeth */}
            <div className="min-w-max">
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="border-r-4 border-t-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.childLowerLeftTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                    <td className="border-t-2 border-green-300">
                      <table className="w-full border-collapse">
                        <tbody>
                          <tr>{renderTeeth(teethData.childLowerRightTeeth)}</tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Enhanced Reason Modal for Multiple Reasons + Diagnosis & Treatment */}
      {reasonModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-60 z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h3 className="text-xl font-semibold text-white">
                {selectedTeeth.some((tooth) => tooth.id === selectedToothId)
                  ? `Edit Findings for Tooth ${selectedToothId}`
                  : `Add Findings for Tooth ${selectedToothId}`}
              </h3>
            </div>

            {/* Content */}
            <div className="p-6 h-[calc(95vh-150px)] overflow-y-auto">
              {/* Display existing reasons */}
              {reasons.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Current O/E Findings:
                  </label>
                  <div className="space-y-2">
                    {reasons.map((reason, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200 shadow-sm"
                      >
                        <span className="text-sm font-medium text-gray-800 flex-1">
                          {reason}
                        </span>
                        <button
                          onClick={() => handleRemoveReason(reason)}
                          className="ml-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full p-1 transition-all duration-200"
                          title="Remove this reason"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add new reason */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Add O/E Findings:
                   <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory("dentalReasons");
                          setIsOpen(!isOpen)
                        }}

                        className="ml-2 text-xs text-blue-600 underline">
                        add
                      </button>
                </label>
                <div className="flex gap-3">
                  <select
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm transition-all duration-200"
                    value=""
                    onChange={(e) => {
                      const selectedReason = e.target.value.trim();
                      if (selectedReason && !reasons.includes(selectedReason)) {
                        setReasons((prev) => [...prev, selectedReason]);
                      }
                      // Reset dropdown
                      e.target.value = "";
                    }}
                  >
                    <option value="" disabled>
                      Select a reason...
                    </option>
                    {dentalReasons
                      .filter((reason) => !reasons.includes(reason))
                      .map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                  </select>
                </div>
              </div>


              {/* Diagnosis */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Diagnosis:
                  <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory("primaryDiagnosis");
                          setIsOpen(!isOpen)
                        }}

                        className="ml-2 text-xs text-blue-600 underline">
                        add
                      </button>
                </label>
                {/* <select
                  value={currentDiagnosis}
                  onChange={handleDiagnosisChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm transition-all duration-200"
                >
                  <option value="">Select diagnosis (optional)...</option>
                  {diagnosisOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select> */}
                <Dropdown
                  item={"diagnosis"} // Change from 'item' to 'selectedUser'
                  selectedOption={currentDiagnosis}
                  onChange={handleDiagnosisChange}
                  listOptions={primaryDiagnosis}
                />
              </div>

              {/* Treatment */}
              <div className="mb-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Treatment:
                  <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory("treatmentPlan");
                          setIsOpen(!isOpen)
                        }}

                        className="ml-2 text-xs text-blue-600 underline">
                        add
                      </button>
                </label>
                {/* <select
                  value={currentTreatment}
                  onChange={handleTreatmentChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm transition-all duration-200"
                >
                  <option value="">Select treatment (optional)...</option>
                  {treatmentPlan.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select> */}
                <Dropdown
                  item={"treatment"}
                  selectedOption={currentTreatment}
                  onChange={handleTreatmentChange}
                  listOptions={treatmentPlan.map(tp => tp.item)}
                />
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                {/* Cancel button */}
                <button
                  className="px-3 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 font-medium text-sm transition-all duration-200 shadow-sm"
                  onClick={() => setReasonModalOpen(false)}
                >
                  Cancel
                </button>

                {/* Action buttons */}
                <div className="flex justify-between gap-3">
                  {selectedTeeth.some((tooth) => tooth.id === selectedToothId) && (
                    <button
                      className="px-3 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm transition-all duration-200 shadow-sm flex items-center gap-2"
                      onClick={handleRemoveTooth}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Remove Tooth
                    </button>
                  )}
                  <button
                    className="px-3 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-all duration-200 shadow-sm flex items-center gap-2"
                    onClick={handleSaveReasons}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Save Findings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <AddCategoryItemModal
                  isOpen={isOpen}
                  selectedCategory={selectedCategory}
                  onClose={() => setIsOpen(false)}
                  onAdded=""
                />
    </div>
  );
};

export default ToothGrid;
